import {
  MonetaryBasis,
  type MonetaryBasisValue,
  type ProspectItem,
  type QuantTransaction,
} from "@/domain/quant/types";
import type { AssetFacts } from "@/domain/quant/asset-facts";
import { wireText } from "@/shared/forms/wire-value";
import type { AdviceSubject } from "@/features/advice-portal/api/advice-subject";

export interface AdvicePick {
  prospectSetId: string;
  prospectPortfolioId: number;
  transactionSetId: string | null;
  quantPortfolioId: string | null;
  portfolioAnalysisId: string | null;
  prospectAnalysisId: string | null;
  startValue: number | null;
  depositAmount: number;
  withdrawalAmount: number;
  reservedAmount: number;
  monetaryBasis: MonetaryBasisValue;
  conditions: AdviceConditions | null;
}

export interface AdviceConditions {
  justificationWishesAndNeeds: string;
  timeHorizonYears: number | null;
  commentTimeHorizon: string;
  sustainabilityView: SustainabilityView | null;
}

export interface AdviceScenarioSeed extends Partial<AdviceConditions> {
  deposit?: number;
  withdrawal?: number;
  reserved?: number;
}

export type SustainabilityView = (typeof SUSTAINABILITY_VIEWS)[number];

export const SUSTAINABILITY_VIEWS = [
  "RISK_AND_RETURN_FOCUS",
  "INTEGRATE_WHERE_POSSIBLE",
  "SUSTAINABILITY_VERY_IMPORTANT",
] as const;

export const REASON_MAX_CHARS = 800;
export const TIME_HORIZON_MAX_YEARS = 999;

export const EMPTY_CONDITIONS: AdviceConditions = {
  justificationWishesAndNeeds: "",
  timeHorizonYears: null,
  commentTimeHorizon: "",
  sustainabilityView: null,
};

export interface PickMaterials {
  items: readonly ProspectItem[];
  trades: readonly QuantTransaction[] | null;
  subject: AdviceSubject | null;
  facts: AssetFacts | null;
}

export type PickRefusal =
  | "PICK_REQUIRED"
  | "PICK_SET_REQUIRED"
  | "PICK_POINT_REQUIRED"
  | "PICK_BASIS_REQUIRED"
  | "PICK_BOTH_FLOWS";

export function readPickBody(
  raw: unknown,
): { ok: true; pick: AdvicePick } | { ok: false; error: PickRefusal } {
  if (typeof raw !== "object" || raw === null) {
    return { ok: false, error: "PICK_REQUIRED" };
  }
  const body = raw as Record<string, unknown>;

  const setId =
    typeof body.prospectSetId === "string"
      ? body.prospectSetId.trim()
      : typeof body.prospectSetId === "number" && Number.isFinite(body.prospectSetId)
        ? String(body.prospectSetId)
        : "";
  if (!setId) return { ok: false, error: "PICK_SET_REQUIRED" };

  const pointRaw =
    typeof body.prospectPortfolioId === "number"
      ? body.prospectPortfolioId
      : Number(body.prospectPortfolioId);
  if (!Number.isFinite(pointRaw)) return { ok: false, error: "PICK_POINT_REQUIRED" };

  const basis =
    body.monetaryBasis === MonetaryBasis.REAL || body.monetaryBasis === MonetaryBasis.NOMINAL
      ? (body.monetaryBasis as MonetaryBasisValue)
      : null;
  if (!basis) return { ok: false, error: "PICK_BASIS_REQUIRED" };

  const deposit = amount(body.depositAmount);
  const withdrawal = amount(body.withdrawalAmount);
  const reserved = amount(body.reservedAmount);
  if (deposit === null || withdrawal === null || reserved === null) {
    return { ok: false, error: "PICK_REQUIRED" };
  }
  if (deposit > 0 && withdrawal > 0) return { ok: false, error: "PICK_BOTH_FLOWS" };

  const startRaw =
    body.startValue === null || body.startValue === undefined
      ? null
      : typeof body.startValue === "number"
        ? body.startValue
        : Number(body.startValue);

  return {
    ok: true,
    pick: {
      prospectSetId: setId,
      prospectPortfolioId: pointRaw,
      transactionSetId: wireText(body.transactionSetId),
      quantPortfolioId: wireText(body.quantPortfolioId),
      portfolioAnalysisId: wireText(body.portfolioAnalysisId),
      prospectAnalysisId: wireText(body.prospectAnalysisId),
      startValue: startRaw !== null && Number.isFinite(startRaw) ? startRaw : null,
      depositAmount: deposit,
      withdrawalAmount: withdrawal,
      reservedAmount: reserved,
      monetaryBasis: basis,
      conditions: conditionsOrNull(narrowAdviceConditions(body.conditions)),
    },
  };
}

export function narrowAdviceConditions(raw: unknown): AdviceConditions {
  if (typeof raw !== "object" || raw === null) return EMPTY_CONDITIONS;
  const c = raw as Record<string, unknown>;
  const years =
    typeof c.timeHorizonYears === "number" ? c.timeHorizonYears : Number(c.timeHorizonYears);
  return {
    justificationWishesAndNeeds: text(c.justificationWishesAndNeeds),
    timeHorizonYears:
      Number.isInteger(years) && years >= 1 && years <= TIME_HORIZON_MAX_YEARS ? years : null,
    commentTimeHorizon: text(c.commentTimeHorizon),
    sustainabilityView: isSustainabilityView(c.sustainabilityView) ? c.sustainabilityView : null,
  };
}

export function conditionsOrNull(conditions: AdviceConditions): AdviceConditions | null {
  const empty =
    conditions.justificationWishesAndNeeds.trim() === "" &&
    conditions.timeHorizonYears === null &&
    conditions.commentTimeHorizon.trim() === "" &&
    conditions.sustainabilityView === null;
  return empty ? null : conditions;
}

export function isSustainabilityView(value: unknown): value is SustainabilityView {
  return (SUSTAINABILITY_VIEWS as readonly unknown[]).includes(value);
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function amount(v: unknown): number | null {
  if (v === undefined || v === null) return 0;
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}
